// components/AuthRedirect.tsx
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchMenuItems, MenuItem } from "../services/menuService";
import { getMyEpcRoles } from "../services/epcProjectService";
import logger from "../utils/logger";

/**
 * AuthRedirect — determines the user's landing page after login.
 *
 * Behavior (per spec §4.12):
 *  - Fetch the user's permitted menu items for their activeDepartment.
 *  - Redirect to the first menu item that has a real path (path !== '#').
 *  - If the menu is empty or the fetch fails:
 *      - Fall back to /dashboard if the user has `dashboard.read` permission.
 *      - Otherwise fall back to /unauthorized.
 *  - While the menu is loading, render a minimal "Redirecting…" placeholder
 *    so we don't flash any UI.
 */

interface FirstRealPath {
  path: string;
  source: string; // for logging — which menu node we picked
}

const findFirstRealPath = (items: MenuItem[]): FirstRealPath | null => {
  for (const item of items) {
    if (item?.path && item.path !== "#") {
      return { path: item.path, source: item.name || item.id };
    }
    if (item?.subItems && item.subItems.length > 0) {
      const nested = findFirstRealPath(item.subItems);
      if (nested) {
        return nested;
      }
    }
  }
  return null;
};

export default function AuthRedirect() {
  const { token, activeDepartment, hasPermission } = useAuth();
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        // EPC PMOs land on their Portfolio Command Center. Best-effort: any
        // failure (non-EPC user, request error) falls through to the standard
        // menu-based landing below.
        try {
          const epcRoles = await getMyEpcRoles();
          if (cancelled) return;
          if (epcRoles.includes("pmo") || epcRoles.includes("epc_head")) {
            logger.debug("AuthRedirect: PMO/EPC Head → /epc/dashboards/pmo");
            setTarget("/epc/dashboards/pmo");
            return;
          }
        } catch (epcErr) {
          logger.debug("AuthRedirect: EPC role check skipped", { epcErr });
        }

        const items = await fetchMenuItems(activeDepartment);
        if (cancelled) return;

        const first = findFirstRealPath(items);
        if (first) {
          logger.debug("AuthRedirect: redirecting to first menu item", {
            path: first.path,
            source: first.source,
            activeDepartment,
          });
          setTarget(first.path);
          return;
        }

        // Menu empty → fall back per spec
        if (hasPermission("dashboard", "read")) {
          logger.debug(
            "AuthRedirect: empty menu, user has dashboard.read → /dashboard",
            { activeDepartment },
          );
          setTarget("/dashboard");
        } else {
          logger.warn(
            "AuthRedirect: empty menu, no dashboard permission → /unauthorized",
            { activeDepartment },
          );
          setTarget("/unauthorized");
        }
      } catch (error) {
        if (cancelled) return;
        // Fetch failed → same fallback policy
        if (hasPermission("dashboard", "read")) {
          logger.warn(
            "AuthRedirect: menu fetch failed, user has dashboard.read → /dashboard",
            { activeDepartment, error },
          );
          setTarget("/dashboard");
        } else {
          logger.error(
            "AuthRedirect: menu fetch failed, no dashboard permission → /unauthorized",
            { activeDepartment, error },
          );
          setTarget("/unauthorized");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, activeDepartment, hasPermission]);

  if (!token) {
    logger.debug("AuthRedirect: no token → /signin");
    return <Navigate to="/signin" replace />;
  }

  if (target) {
    return <Navigate to={target} replace />;
  }

  // Loading state — keep it minimal so nothing flashes.
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen items-center justify-center bg-white text-gray-600"
    >
      <div className="flex flex-col items-center gap-3">
        <svg
          className="h-6 w-6 animate-spin text-[#264f3c]"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span className="text-sm">Redirecting…</span>
      </div>
    </div>
  );
}
